
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ListingCard from '@/components/ListingCard';
import Map from '@/components/Map';
import { MessageBreederButton } from '@/components/messaging/MessageBreederButton';
import { useAnnouncementListings } from '@/hooks/useAnnouncementListings';
import { useBreederProfile } from '@/hooks/useBreederProfile';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const AnnouncementDetail = () => {
  const { id } = useParams();

  // Fetch announcement details
  const { data: announcement, isLoading: announcementLoading, error: announcementError } = useQuery({
    queryKey: ['announcement', id],
    queryFn: async () => {
      if (!id) throw new Error('No announcement ID provided');
      
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch breeder profile using the announcement's breeder_id
  const { data: profile, isLoading: profileLoading } = useBreederProfile({ 
    breederId: announcement?.breeder_id || '' 
  });

  // Fetch listings for this announcement
  const { data: listings = [], isLoading: listingsLoading } = useAnnouncementListings({ 
    announcementId: id || '' 
  });

  // Create breeder location data for the map
  const breederMapData = React.useMemo(() => {
    if (!profile) return null;

    let lat = 40.7128; // Default NYC lat
    let lng = -74.0060; // Default NYC lng

    if (profile.locationCoordinates) {
      try {
        let coordinates;
        
        if (typeof profile.locationCoordinates === 'string') {
          coordinates = JSON.parse(profile.locationCoordinates);
        } else {
          coordinates = profile.locationCoordinates;
        }
        
        if (coordinates && typeof coordinates === 'object' && 
            'longitude' in coordinates && 'latitude' in coordinates &&
            typeof coordinates.longitude === 'number' && 
            typeof coordinates.latitude === 'number') {
          lat = coordinates.latitude;
          lng = coordinates.longitude;
        }
      } catch (error) {
        console.log('Error parsing coordinates:', error);
      }
    }

    return [{
      id: 1,
      name: profile.name,
      location: profile.location,
      lat: lat,
      lng: lng
    }];
  }, [profile]);

  // Transform listings for ListingCard component
  const transformedListings = listings.map(listing => ({
    id: listing.id,
    title: listing.title,
    image: Array.isArray(listing.images) && listing.images.length > 0 
      ? (listing.images[0] as string)
      : 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop',
    price: listing.price ? `$${listing.price.toLocaleString()}` : undefined,
    location: listing.location || 'Location not specified',
    breeder: listing.profiles?.business_name || 
      `${listing.profiles?.first_name || ''} ${listing.profiles?.last_name || ''}`.trim() || 
      'Unknown Breeder',
    breed: listing.breed,
    evaluationData: {
      calvingEase: listing.calving_ease,
      birthWeight: listing.birth_weight,
      weaningWeight: listing.weaning_weight,
      milk: listing.milk,
    }
  }));

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Invalid announcement ID.</p>
          </div>
        </div>
      </div>
    );
  }

  if (announcementLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  if (announcementError || !announcement || !profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">Unable to load announcement details.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to={`/breeder/${announcement.breeder_id}`} 
          className="inline-flex items-center text-green-700 hover:text-green-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Link>

        {/* Announcement Header */}
        <div className="bg-green-50 border-green-200 border rounded-lg shadow-sm mb-8 p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{announcement.title}</h1>
          {announcement.description && (
            <p className="text-gray-700 leading-relaxed text-lg">{announcement.description}</p>
          )}
        </div>

        {/* Profile Header with Cover and Profile Image */}
        <div className="bg-white rounded-lg shadow-sm mb-8 overflow-hidden">
          {/* Cover Image Section */}
          <div className="relative h-48 bg-stone-200">
            {profile.coverImage ? (
              <img 
                src={profile.coverImage} 
                alt="Cover"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-stone-200 via-stone-300 to-stone-400" />
            )}
            
            {/* Profile Image - positioned to overlap cover image boundary */}
            <div className="absolute -bottom-12 left-6 z-30">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-stone-200 border-4 border-white shadow-lg overflow-hidden">
                  {profile.profileImage ? (
                    <img 
                      src={profile.profileImage} 
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-stone-200 via-stone-300 to-stone-400" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-20 px-8 pb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{profile.name}</h2>
                <div className="flex items-center text-gray-600 mb-4">
                  <span>{profile.location}</span>
                </div>
              </div>
              <MessageBreederButton
                listingId={`announcement-${id}`}
                breederId={announcement.breeder_id}
                listingTitle={`Message about ${announcement.title}`}
                listingUrl={window.location.href}
                className="bg-green-700 hover:bg-green-800"
              />
            </div>

            {/* Breeds badges */}
            {profile?.breeds && profile.breeds.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.breeds.map((breed, index) => (
                  <Badge key={index} className="bg-green-100 text-sm text-green-800 hover:bg-green-200">
                    {breed}
                  </Badge>
                ))}
              </div>
            )}

            <p className="text-gray-700 leading-relaxed mb-4">{profile.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <div className="text-sm text-gray-600">Total Listings in this Auction</div>
                <div className="font-medium">{transformedListings.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Listings Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Auction Listings</h2>
          {listingsLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700"></div>
            </div>
          ) : transformedListings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No listings available for this auction yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {transformedListings.map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          )}
        </div>

        {/* Map Section - Only render when profile data and map data are available */}
        {profile && breederMapData && (
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Location</h2>
            <Map 
              breeders={breederMapData} 
              center={[breederMapData[0].lng, breederMapData[0].lat]} 
              zoom={10} 
              height="400px" 
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AnnouncementDetail;
